"use client";

import React, { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { Input } from "../../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import {
  Users,
  TrendingUp,
  Calculator,
  Play,
  Download,
  FileText,
  Plus,
  Minus,
  BarChart3,
  LineChart as LineChartIcon,
  Loader2,
  MessageSquare,
  Save,
} from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { 
  getProcedureFileUsage, 
  updateProcedureFileUsage,
  type FileUsageEntry
} from "@/utils/fileUsageReference";
import { formatNumber } from "@/utils/formatting";

interface HeadcountReconciliationProps {
  onBack?: () => void;
}

interface MonthlyData {
  month: string;
  opening: number;
  joiners: number;
  leavers: number;
  closing: number;
}

interface QuarterlyData {
  quarter: string;
  weightedFigure: number;
  totalWeight: number;
  weightedAverage: number;
}

interface AnnualData {
  year: string;
  weightedFigure: number;
  totalWeight: number;
  weightedAverage: number;
}

interface QuarterlyPayData {
  quarter: string;
  grossPay: number;
  weightedAverageHeadcount: number;
  averagePay: number;
}

const MONTHS = [
  "Apr-24", "May-24", "Jun-24", "Jul-24", "Aug-24", "Sep-24",
  "Oct-24", "Nov-24", "Dec-24", "Jan-25", "Feb-25", "Mar-25"
];

const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

export default function HeadcountReconciliation({ onBack }: HeadcountReconciliationProps) {
  const { isArchivalMode } = useArchivalMode();
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [quarterlyData, setQuarterlyData] = useState<QuarterlyData[]>([]);
  const [annualData, setAnnualData] = useState<AnnualData[]>([]);
  const [quarterlyPayData, setQuarterlyPayData] = useState<QuarterlyPayData[]>([]);
  const [openingHeadcount, setOpeningHeadcount] = useState<number>(2000);
  const [ctcReportCount, setCtcReportCount] = useState<number>(0);
  const [reconciliationRows, setReconciliationRows] = useState<Array<{ id: string; description: string; amount: number }>>([]);
  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [showCharts, setShowCharts] = useState(true);
  const [fileUrl, setFileUrl] = useState<string>("");

  // Table data state (similar to Increment Analysis)
  const [tableData, setTableData] = useState<{
    "As per Above Headcount (A)": number;
    "As per CTC Report (B)": number;
    "Difference (C)": number;
    "User reconciliations": Array<{ Particulars: string; Employees: number }>;
    "Net Difference": number;
  } | null>(null);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [tableFileNotFound, setTableFileNotFound] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // IPE-style client file dropdowns
  const [clientFiles, setClientFiles] = useState<Array<{ name: string; reference: string }>>([]);
  const [isLoadingClientFiles, setIsLoadingClientFiles] = useState(false);
  const [payRegistrar, setPayRegistrar] = useState<string>("");
  const [ctcFile, setCtcFile] = useState<string>("");
  const [addList, setAddList] = useState<string>("");
  const [delList, setDelList] = useState<string>("");
  const [addColumns, setAddColumns] = useState<string[]>([]);
  const [delColumns, setDelColumns] = useState<string[]>([]);
  const [dojal, setDojal] = useState<string>("");
  const [doldl, setDoldl] = useState<string>("");
  const [openingHeadcountInput, setOpeningHeadcountInput] = useState<number>(2000);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceQuestion, setInferenceQuestion] = useState<any>({ textResponse: null, documents: [] });
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `HEADCOUNT_RECONCILIATION_${normalizedSection}`;
  };

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load inference question from Execution_EBE_Responses.json
  useEffect(() => {
    const loadInferenceQuestion = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.("Execution_EBE_Responses.json");
        if (result?.success && result?.data?.substantiveProcedures) {
          const inferenceId = "HEADCOUNT-RECONCILIATION-INFERENCE";
          const inferenceData = result.data.substantiveProcedures[inferenceId];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load inference question:", error);
      }
    };
    loadInferenceQuestion();
  }, []);

  useEffect(() => {
    loadClientFiles();
    loadFileUsageReference();
  }, []);

  const loadFileUsageReference = async () => {
    try {
      const usage = await getProcedureFileUsage("Headcount Reconciliation");
      setFileUsageReference(usage);
    } catch (error) {
      console.error("Error loading file usage reference:", error);
    }
  };

  const loadClientFiles = async () => {
    setIsLoadingClientFiles(true);
    try {
      console.log("Loading client files with Execution_EBE section...");

      // Try the specific search for Execution_EBE files
      if (window.sharePointAPI?.loadComcFiles) {
        console.log("Using loadComcFiles API for Execution_EBE");
        const result = await window.sharePointAPI.loadComcFiles("Execution_EBE");
        console.log("API Response:", result);

        if (result && result.success && result.data && result.data.files) {
          const files = result.data.files.map((f: any) => ({
            name: f.name || f,
            reference: f.reference || ""
          }));
          console.log("Found files with Execution_EBE section:", files);
          setClientFiles(files);
          return;
        } else {
          console.log("No files found with Execution_EBE section");
        }
      }

      // If no Execution_EBE files found, try to load all files as fallback
      console.log("Falling back to loadCloudFiles...");
      if (window.sharePointAPI?.loadCloudFiles) {
        const fallbackResult = await window.sharePointAPI.loadCloudFiles();
        console.log("Fallback API Response:", fallbackResult);

        if (fallbackResult && fallbackResult.success && fallbackResult.data && fallbackResult.data.files) {
          const files = fallbackResult.data.files.map((f: any) => ({
            name: f.name,
            reference: f.reference || ""
          }));
          console.log("Fallback files loaded:", files);
          setClientFiles(files);
        } else {
          console.log("No files found in fallback either");
          setClientFiles([]);
        }
      } else {
        console.log("SharePoint API not available");
        setClientFiles([]);
      }
    } catch (error) {
      console.error("Error loading client files:", error);
      setClientFiles([]);
    } finally {
      setIsLoadingClientFiles(false);
    }
  };

  // Initialize monthly data
  useEffect(() => {
    const initialData: MonthlyData[] = MONTHS.map((month, index) => ({
      month,
      opening: index === 0 ? openingHeadcount : 0,
      joiners: 0,
      leavers: 0,
      closing: 0,
    }));
    setMonthlyData(initialData);
  }, [openingHeadcount]);

  // Calculate closing headcount for each month
  useEffect(() => {
    setMonthlyData(prev => {
      const updated = [...prev];
      for (let i = 0; i < updated.length; i++) {
        if (i === 0) {
          updated[i].closing = updated[i].opening + updated[i].joiners - updated[i].leavers;
        } else {
          updated[i].opening = updated[i - 1].closing;
          updated[i].closing = updated[i].opening + updated[i].joiners - updated[i].leavers;
        }
      }
      return updated;
    });
  }, [monthlyData.map(m => m.joiners).join(','), monthlyData.map(m => m.leavers).join(',')]);

  // Calculate quarterly weighted averages
  useEffect(() => {
    const quarterly: QuarterlyData[] = [];
    for (let q = 0; q < 4; q++) {
      const startMonth = q * 3;
      const months = monthlyData.slice(startMonth, startMonth + 3);

      if (months.length === 3) {
        const weightedFigure = (months[0].closing * 3) + (months[1].closing * 2) + (months[2].closing * 1);
        const totalWeight = 6;
        const weightedAverage = weightedFigure / totalWeight;

        quarterly.push({
          quarter: QUARTERS[q],
          weightedFigure,
          totalWeight,
          weightedAverage: Math.round(weightedAverage * 100) / 100,
        });
      }
    }
    setQuarterlyData(quarterly);
  }, [monthlyData]);

  // Calculate annual weighted average
  useEffect(() => {
    if (monthlyData.length === 12) {
      const weights = [12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1];
      const weightedFigure = monthlyData.reduce((sum, month, index) =>
        sum + (month.closing * weights[index]), 0
      );
      const totalWeight = 78; // Sum of weights 12+11+...+1
      const weightedAverage = weightedFigure / totalWeight;

      setAnnualData([{
        year: "FY24-25",
        weightedFigure,
        totalWeight,
        weightedAverage: Math.round(weightedAverage * 100) / 100,
      }]);
    }
  }, [monthlyData]);

  // Remove simulated quarterly pay; will be set from JSON

  const handleMonthlyDataChange = (monthIndex: number, field: keyof MonthlyData, value: number) => {
    setMonthlyData(prev => {
      const updated = [...prev];
      updated[monthIndex] = { ...updated[monthIndex], [field]: value };
      return updated;
    });
  };

  const addReconciliationRow = () => {
    const newRow = {
      id: `recon_${Date.now()}`,
      description: "",
      amount: 0,
    };
    setReconciliationRows(prev => [...prev, newRow]);
  };

  const removeReconciliationRow = (id: string) => {
    setReconciliationRows(prev => prev.filter(row => row.id !== id));
  };

  const updateReconciliationRow = (id: string, field: string, value: string | number) => {
    setReconciliationRows(prev =>
      prev.map(row =>
        row.id === id ? { ...row, [field]: value } : row
      )
    );
  };

  const calculateNetDifference = () => {
    const headcountDifference = monthlyData[11]?.closing - ctcReportCount || 0;
    const reconciliationTotal = reconciliationRows.reduce((sum, row) => sum + row.amount, 0);
    return headcountDifference - reconciliationTotal;
  };

  const loadTableData = async () => {
    setIsLoadingTable(true);
    setTableFileNotFound(false);
    try {
      if (!window.sharePointAPI?.downloadHeadcountReconciliationJson) {
        throw new Error("SharePoint API method 'downloadHeadcountReconciliationJson' is not available. Please restart the application.");
      }
      const result = await window.sharePointAPI.downloadHeadcountReconciliationJson();

      if (result?.success && result?.data) {
        const data = result.data;
        const testRecon = data.Test_Reconciliation;

        // Check if Test_Reconciliation exists and has data
        if (testRecon && typeof testRecon === 'object') {
          // Helper function to find value from User reconciliations array
          const findValueFromReconciliations = (searchTerms: string[]): number | null => {
            if (!Array.isArray(testRecon["User reconciliations"])) return null;
            const found = testRecon["User reconciliations"].find((r: any) => {
              const particulars = (r.Particulars || "").trim().toLowerCase();
              return searchTerms.some(term => particulars === term.toLowerCase());
            });
            return found !== undefined ? Number(found.Employees || 0) : null;
          };

          // Helper to check if value is valid (not null, undefined, or 0)
          const isValidValue = (val: any): boolean => {
            return val !== null && val !== undefined && val !== 0;
          };

          // Priority: User reconciliations array (has actual data) > direct properties > calculated defaults
          const reconAboveHeadcount = findValueFromReconciliations(["As per Above Headcount", "As per Above Headcount (A)"]);
          const reconCtcReport = findValueFromReconciliations(["As per CTC Report", "As per CTC Report (B)"]);
          const reconDifference = findValueFromReconciliations(["Difference", "Difference (C)"]);
          const reconNetDiff = findValueFromReconciliations(["Net Difference"]);

          const aboveHeadcount = isValidValue(reconAboveHeadcount)
            ? reconAboveHeadcount
            : (isValidValue(testRecon["As per Above Headcount (A)"])
              ? testRecon["As per Above Headcount (A)"]
              : (monthlyData[11]?.closing || 0));

          const ctcReport = isValidValue(reconCtcReport)
            ? reconCtcReport
            : (isValidValue(testRecon["As per CTC Report (B)"])
              ? testRecon["As per CTC Report (B)"]
              : ctcReportCount);

          const difference = isValidValue(reconDifference)
            ? reconDifference
            : (isValidValue(testRecon["Difference (C)"])
              ? testRecon["Difference (C)"]
              : (aboveHeadcount - ctcReport));

          const netDiff = isValidValue(reconNetDiff)
            ? reconNetDiff
            : (isValidValue(testRecon["Net Difference"])
              ? testRecon["Net Difference"]
              : calculateNetDifference());

          // Load the complete table structure
          const loadedTableData = {
            "As per Above Headcount (A)": aboveHeadcount,
            "As per CTC Report (B)": ctcReport,
            "Difference (C)": difference,
            "User reconciliations": Array.isArray(testRecon["User reconciliations"])
              ? testRecon["User reconciliations"]
              : [],
            "Net Difference": netDiff
          };

          setTableData(loadedTableData);

          // Update ctcReportCount state
          if (ctcReport !== ctcReportCount) {
            setCtcReportCount(ctcReport);
          }

          // Load user reconciliations (description rows)
          // Filter out standard rows that are already displayed as static rows
          const standardRowNames = [
            "As per Above Headcount",
            "As per Above Headcount (A)",
            "As per CTC Report",
            "As per CTC Report (B)",
            "Difference",
            "Difference (C)",
            "Net Difference"
          ];

          if (Array.isArray(testRecon["User reconciliations"]) && testRecon["User reconciliations"].length > 0) {
            const userRows = testRecon["User reconciliations"]
              .filter((r: any) => {
                const particulars = (r.Particulars || "").trim();
                // Exclude standard rows - only keep user-added custom rows
                return !standardRowNames.some(name =>
                  particulars.toLowerCase() === name.toLowerCase()
                );
              })
              .map((r: any, idx: number) => ({
                id: `recon_${Date.now()}_${idx}`,
                description: r.Particulars || "",
                amount: Number(r.Employees || 0)
              }));
            setReconciliationRows(userRows);
          } else {
            setReconciliationRows([]);
          }

          setTableFileNotFound(false);
        } else {
          // No Test_Reconciliation data found
          setTableFileNotFound(true);
          setTableData(null);
          setReconciliationRows([]);
        }
      } else {
        setTableFileNotFound(true);
        setTableData(null);
        setReconciliationRows([]);
      }
    } catch (error) {
      console.error("Error loading table data:", error);
      setTableFileNotFound(true);
      setTableData(null);
      setReconciliationRows([]);
    } finally {
      setIsLoadingTable(false);
    }
  };

  const uploadTableData = async () => {
    if (!tableData && monthlyData.length === 0) {
      alert("Please calculate headcount reconciliation first");
      return;
    }

    setIsUploading(true);
    try {
      // Get current values - prefer tableData if loaded from SharePoint, fallback to calculated
      const currentAboveHeadcount = tableData?.["As per Above Headcount (A)"] || monthlyData[11]?.closing || 0;
      const currentCtcReport = tableData?.["As per CTC Report (B)"] || ctcReportCount;
      const currentDifference = tableData?.["Difference (C)"] || (currentAboveHeadcount - currentCtcReport);
      const userReconciliationsSum = reconciliationRows.reduce((sum, row) => sum + (row.amount || 0), 0);
      const currentNetDifference = currentDifference - userReconciliationsSum;

      // Prepare the complete table data structure
      const tableDataToUpload = {
        "As per Above Headcount (A)": currentAboveHeadcount,
        "As per CTC Report (B)": currentCtcReport,
        "Difference (C)": currentDifference,
        "User reconciliations": reconciliationRows.map(r => ({
          Particulars: r.description || "",
          Employees: Number(r.amount || 0)
        })),
        "Net Difference": currentNetDifference
      };

      // Upload using SharePoint API
      if (window.sharePointAPI?.uploadHeadcountReconciliationUserRows) {
        const result = await window.sharePointAPI.uploadHeadcountReconciliationUserRows(tableDataToUpload);

        if (result.success) {
          alert("Successfully uploaded headcount reconciliation table data to SharePoint!");
          // Reload table data after successful upload
          await loadTableData();
        } else {
          throw new Error(result.error || "Upload failed");
        }
      } else {
        throw new Error("SharePoint API not available");
      }
    } catch (error) {
      console.error("Error uploading data:", error);
      alert("Failed to upload data: " + (error instanceof Error ? error.message : "Unknown error"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleLoadColumns = async () => {
    try {
      const entries: FileUsageEntry[] = [];

      if (window.payroll?.loadExcelColumns) {
        if (addList) {
          const resAdd = await window.payroll.loadExcelColumns(addList.includes(' (') ? addList.split(' (')[0] : addList);
          if (resAdd.ok && resAdd.columns) {
            setAddColumns(resAdd.columns);
            entries.push({ "Additions Listing": addList.includes(' (') ? addList.split(' (')[0] : addList });
          }
        }
        if (delList) {
          const resDel = await window.payroll.loadExcelColumns(delList.includes(' (') ? delList.split(' (')[0] : delList);
          if (resDel.ok && resDel.columns) {
            setDelColumns(resDel.columns);
            entries.push({ "Deletions Listing": delList.includes(' (') ? delList.split(' (')[0] : delList });
          }
        }
      }

      // Save file names to file usage reference
      if (entries.length > 0) {
        await updateProcedureFileUsage("Headcount Reconciliation", entries);
        await loadFileUsageReference();
      }
    } catch { }
  };

  const canCalculate = () => {
    return !!(payRegistrar && ctcFile && addList && delList && dojal && doldl);
  };

  const runHeadcountReconciliation = async () => {
    // Save all file names and column selections to file usage reference
    const entries: FileUsageEntry[] = [];
    if (payRegistrar) {
      const fileName = payRegistrar.includes(' (') ? payRegistrar.split(' (')[0] : payRegistrar;
      entries.push({ "Pay Register": fileName });
    }
    if (ctcFile) {
      const fileName = ctcFile.includes(' (') ? ctcFile.split(' (')[0] : ctcFile;
      entries.push({ "CTC Report": fileName });
    }
    if (addList) {
      const fileName = addList.includes(' (') ? addList.split(' (')[0] : addList;
      entries.push({ "Additions Listing": fileName });
    }
    if (delList) {
      const fileName = delList.includes(' (') ? delList.split(' (')[0] : delList;
      entries.push({ "Deletions Listing": fileName });
    }
    // Save column selections from Map Date Columns
    if (dojal) {
      entries.push({ "Date of joining (from Additions Listing)": dojal });
    }
    if (doldl) {
      entries.push({ "Date of leaving (from Deletions Listing)": doldl });
    }
    if (entries.length > 0) {
      await updateProcedureFileUsage("Headcount Reconciliation", entries);
      await loadFileUsageReference();
    }

    setProcessingStatus("running");
    setFileUrl("");
    try {
      if (window.payroll?.run) {
        const result = await window.payroll.run("execute_headcount_sharepoint", {
          inputFiles: [],
          options: {
            add_list: addList,
            del_list: delList,
            ctc_file: ctcFile,
            pay_registrar: payRegistrar,
            opening_apr: openingHeadcountInput,
            dojal,
            doldl,
          },
        });
        if (result.ok) {
          try {
            const lines = String(result.stdout || '').split('\n');
            const jsonLine = lines.find((l: string) => l.trim().startsWith('{') && l.trim().endsWith('}'));
            if (jsonLine) {
              const parsed = JSON.parse(jsonLine);
              if (parsed.success) {
                setProcessingStatus('completed');
                if (parsed.file_web_url) setFileUrl(parsed.file_web_url);
                const data = parsed.data;
                if (data) {
                  // Monthly
                  if (Array.isArray(data.Headcount_Reconciliation)) {
                    const m = data.Headcount_Reconciliation.map((r: any) => ({
                      month: r.Month,
                      opening: Number(r.Opening),
                      joiners: Number(r.Joiners),
                      leavers: Number(r.Leavers),
                      closing: Number(r.Closing),
                    }));
                    setMonthlyData(m);
                    if (m.length > 0) setOpeningHeadcount(m[0].opening);
                  }
                  // Test Reconciliation
                  if (Array.isArray(data.Test_Reconciliation) && data.Test_Reconciliation.length >= 2) {
                    setCtcReportCount(Number(data.Test_Reconciliation[1].Employees) || 0);
                  }
                  // Quarterly Weighted
                  if (data.Quarterly_Weighted_Average_Headcount) {
                    const qd: QuarterlyData[] = Object.entries(data.Quarterly_Weighted_Average_Headcount).map(([q, v]: any) => ({
                      quarter: q,
                      weightedFigure: Number(v['Weighted Figure']),
                      totalWeight: Number(v['Total Weight']),
                      weightedAverage: Number(v['Weighted Average']),
                    }));
                    setQuarterlyData(qd);
                  }
                  // Annual
                  if (data.Annual_Weighted_Average_Headcount) {
                    const ad = data.Annual_Weighted_Average_Headcount;
                    setAnnualData([{
                      year: String(ad['Year']),
                      weightedFigure: Number(ad['Weighted Figure']),
                      totalWeight: Number(ad['Total Weight']),
                      weightedAverage: Number(ad['Weighted Average']),
                    }]);
                  }
                  // Average Gross Pay Quarterly
                  if (data.Average_Gross_Pay_Quarterly) {
                    const qp: QuarterlyPayData[] = Object.entries(data.Average_Gross_Pay_Quarterly).map(([q, v]: any) => ({
                      quarter: String(q),
                      grossPay: Number(v['Gross Pay']),
                      weightedAverageHeadcount: Number(v['Weighted Average Headcount']),
                      averagePay: Number(v['Average Pay']),
                    }));
                    setQuarterlyPayData(qp);
                  }
                }
              } else {
                setProcessingStatus('error');
              }
            } else {
              setProcessingStatus('completed');
            }
          } catch {
            setProcessingStatus('completed');
          }
        } else {
          setProcessingStatus('error');
        }
      }
    } catch (e) {
      setProcessingStatus('error');
    }
  };

  const displayHeadcountFromJson = async () => {
    setProcessingStatus("running");
    try {
      // Call SharePoint API to download JSON
      if (!window.sharePointAPI?.downloadHeadcountReconciliationJson) {
        throw new Error("SharePoint API method 'downloadHeadcountReconciliationJson' is not available. Please restart the application.");
      }
      const result = await window.sharePointAPI.downloadHeadcountReconciliationJson();

      if (result?.success && result?.data) {
        const data = result.data;

        // Monthly Headcount Reconciliation
        if (Array.isArray(data.Headcount_Reconciliation)) {
          const m = data.Headcount_Reconciliation.map((r: any) => ({
            month: r.Month,
            opening: Number(r.Opening),
            joiners: Number(r.Joiners),
            leavers: Number(r.Leavers),
            closing: Number(r.Closing),
          }));
          setMonthlyData(m);
          if (m.length > 0) setOpeningHeadcount(m[0].opening);
        }

        // Test Reconciliation
        if (Array.isArray(data.Test_Reconciliation) && data.Test_Reconciliation.length >= 2) {
          setCtcReportCount(Number(data.Test_Reconciliation[1].Employees) || 0);
        }

        // Quarterly Weighted Average Headcount
        if (data.Quarterly_Weighted_Average_Headcount) {
          const qd: QuarterlyData[] = Object.entries(data.Quarterly_Weighted_Average_Headcount).map(([q, v]: any) => ({
            quarter: q,
            weightedFigure: Number(v['Weighted Figure']),
            totalWeight: Number(v['Total Weight']),
            weightedAverage: Number(v['Weighted Average']),
          }));
          setQuarterlyData(qd);
        }

        // Annual Weighted Average Headcount
        if (data.Annual_Weighted_Average_Headcount) {
          const ad = data.Annual_Weighted_Average_Headcount;
          setAnnualData([{
            year: String(ad['Year']),
            weightedFigure: Number(ad['Weighted Figure']),
            totalWeight: Number(ad['Total Weight']),
            weightedAverage: Number(ad['Weighted Average']),
          }]);
        }

        // Average Gross Pay Quarterly
        if (data.Average_Gross_Pay_Quarterly) {
          const qp: QuarterlyPayData[] = Object.entries(data.Average_Gross_Pay_Quarterly).map(([q, v]: any) => ({
            quarter: String(q),
            grossPay: Number(v['Gross Pay']),
            weightedAverageHeadcount: Number(v['Weighted Average Headcount']),
            averagePay: Number(v['Average Pay']),
          }));
          setQuarterlyPayData(qp);
        }

        setProcessingStatus('completed');
      } else {
        throw new Error(result?.error || "Failed to load headcount reconciliation data");
      }
    } catch (error) {
      console.error("Error loading headcount reconciliation data:", error);
      alert("Failed to load headcount reconciliation data: " + (error instanceof Error ? error.message : "Unknown error"));
      setProcessingStatus('error');
    }
  };

  const chartData = [
    ...quarterlyPayData.map(q => ({
      quarter: q.quarter,
      "Average Gross Pay": q.averagePay,
      "Weighted Average Headcount": q.weightedAverageHeadcount,
    }))
  ];

  // Auto-display Headcount Reconciliation JSON on mount
  useEffect(() => {
    // Trigger the same functionality as clicking "Display Headcount Reconciliation"
    displayHeadcountFromJson();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-load table data when processing completes (on mount or after display)
  useEffect(() => {
    if (processingStatus === "completed") {
      loadTableData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processingStatus]);

  // Save inference question
  const saveInferenceQuestion = async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus('error');
      return;
    }

    setInferenceSaveStatus('saving');
    setInferenceSaveError(null);

    try {
      // Auto-save any pending textbox content
      const documentManagers = document.querySelectorAll('[data-document-manager]');
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute('data-item-id');
        if (itemId) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
          if (autoSaveMethod && typeof autoSaveMethod === 'function') {
            autoSaveMethod();
          }
        }
      }

      await new Promise(resolve => setTimeout(resolve, 1500));

      const areaPrefix = "EBE";
      const fileName = `Execution_${areaPrefix}_Responses.json`;
      
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceId = "HEADCOUNT-RECONCILIATION-INFERENCE";
      const sectionToFind = `Execution_${areaPrefix}_${inferenceId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponseData = null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponseData = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      } else if (inferenceQuestion.textResponse) {
        inferenceTextResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[inferenceId] = {
        selectedOption: "",
        textResponse: inferenceTextResponseData,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus('saved');
      setTimeout(() => setInferenceSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save inference question:", error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus('error');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Headcount Reconciliation</h2>
          <p className="text-gray-400">
            Monthly reconciliation and analytical procedures for headcount data
          </p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            ← Back
          </Button>
        )}
      </div>



      {/* Inputs: Select files */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Users className="h-5 w-5" />
              Select Source Files
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Select Source Files"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Load Client Files button removed as per requirements */}
          {clientFiles.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-white">Pay Register</Label>
                <Select value={payRegistrar} onValueChange={async (value) => {
                  setPayRegistrar(value);
                  // Save file name to file usage reference when selected
                  if (value) {
                    const fileName = value.includes(' (') ? value.split(' (')[0] : value;
                    await updateProcedureFileUsage("Headcount Reconciliation", [{ "Pay Register": fileName }]);
                    await loadFileUsageReference();
                  }
                }}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white max-h-64">
                    {clientFiles.map((f, i) => (<SelectItem key={i} value={f.name}>{f.name} {f.reference && `(${f.reference})`}</SelectItem>))}
                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["Pay Register"]) && (
                  <p className="text-xs text-white/60 mt-1">
                    Last used: {fileUsageReference.find(e => e["Pay Register"])?.["Pay Register"]}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-white">CTC Report</Label>
                <Select value={ctcFile} onValueChange={async (value) => {
                  setCtcFile(value);
                  // Save file name to file usage reference when selected
                  if (value) {
                    const fileName = value.includes(' (') ? value.split(' (')[0] : value;
                    await updateProcedureFileUsage("Headcount Reconciliation", [{ "CTC Report": fileName }]);
                    await loadFileUsageReference();
                  }
                }}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white max-h-64">
                    {clientFiles.map((f, i) => (<SelectItem key={i} value={f.name}>{f.name} {f.reference && `(${f.reference})`}</SelectItem>))}
                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["CTC Report"]) && (
                  <p className="text-xs text-white/60 mt-1">
                    Last used: {fileUsageReference.find(e => e["CTC Report"])?.["CTC Report"]}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-white">Additions Listing</Label>
                <Select value={addList} onValueChange={setAddList}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white max-h-64">
                    {clientFiles.map((f, i) => (<SelectItem key={i} value={f.name}>{f.name} {f.reference && `(${f.reference})`}</SelectItem>))}
                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["Additions Listing"]) && (
                  <p className="text-xs text-white/60 mt-1">
                    Last used: {fileUsageReference.find(e => e["Additions Listing"])?.["Additions Listing"]}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-white">Deletions Listing</Label>
                <Select value={delList} onValueChange={setDelList}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white max-h-64">
                    {clientFiles.map((f, i) => (<SelectItem key={i} value={f.name}>{f.name} {f.reference && `(${f.reference})`}</SelectItem>))}
                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["Deletions Listing"]) && (
                  <p className="text-xs text-white/60 mt-1">
                    Last used: {fileUsageReference.find(e => e["Deletions Listing"])?.["Deletions Listing"]}
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Columns selection */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Users className="h-5 w-5" />
              Map Date Columns
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Map Date Columns"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Button onClick={handleLoadColumns} disabled={!addList && !delList} className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Get Columns
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-white">Date of joining (from Additions Listing)</Label>
              <Select value={dojal} onValueChange={setDojal}>
                <SelectTrigger><SelectValue placeholder="Select column..." /></SelectTrigger>
                <SelectContent className="border-white/10 bg-black/90 text-white max-h-64">
                  {addColumns.map((c, i) => (<SelectItem key={i} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
              {fileUsageReference.find(e => e["Date of joining (from Additions Listing)"]) && (
                <p className="text-xs text-white/60 mt-1">
                  Last used: {fileUsageReference.find(e => e["Date of joining (from Additions Listing)"])?.["Date of joining (from Additions Listing)"]}
                </p>
              )}
            </div>
            <div>
              <Label className="text-white">Date of leaving (from Deletions Listing)</Label>
              <Select value={doldl} onValueChange={setDoldl}>
                <SelectTrigger><SelectValue placeholder="Select column..." /></SelectTrigger>
                <SelectContent className="border-white/10 bg-black/90 text-white max-h-64">
                  {delColumns.map((c, i) => (<SelectItem key={i} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
              {fileUsageReference.find(e => e["Date of leaving (from Deletions Listing)"]) && (
                <p className="text-xs text-white/60 mt-1">
                  Last used: {fileUsageReference.find(e => e["Date of leaving (from Deletions Listing)"])?.["Date of leaving (from Deletions Listing)"]}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Opening Headcount Input (moved here after Map Date Columns) */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Users className="h-5 w-5" />
              Opening Headcount
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Opening Headcount"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="space-y-2">
              <Label className="text-white">Opening Headcount (April)</Label>
              <Input
                type="number"
                value={openingHeadcountInput}
                onChange={(e) => setOpeningHeadcountInput(Number(e.target.value))}
                className="w-32 border-white/10 bg-black/40 text-white"
              />
            </div>
            <div className="text-sm text-gray-400">
              For initial audit: enter manually. For subsequent audits: previous year's closing will be used.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Reconciliation Table - only show after calculation */}
      {processingStatus === "completed" && (
        <Card className="border-white/10 bg-black/40">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-3 text-white">
                <Users className="h-5 w-5" />
                Monthly Headcount Reconciliation
              </CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Monthly Headcount Reconciliation"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-white">Month</TableHead>
                      <TableHead className="text-white">Opening (A)</TableHead>
                      <TableHead className="text-white">Joiners (B)</TableHead>
                      <TableHead className="text-white">Leavers (C)</TableHead>
                      <TableHead className="text-white">Closing (A+B-C)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthlyData.map((month) => (
                      <TableRow key={month.month}>
                        <TableCell className="text-white">{month.month}</TableCell>
                        <TableCell className="text-white">{month.opening}</TableCell>
                        <TableCell className="text-white">{month.joiners}</TableCell>
                        <TableCell className="text-white">{month.leavers}</TableCell>
                        <TableCell className="text-white font-medium">
                          {month.closing}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test Reconciliation - only show after calculation */}
      {processingStatus === "completed" && (
        <Card className="border-white/10 bg-black/40">
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-white">
              <div className="flex items-center gap-3">
                <Calculator className="h-5 w-5" />
                Test Reconciliation
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Test Reconciliation"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-white/10"
                  onClick={loadTableData}
                  disabled={isLoadingTable}
                >
                  {isLoadingTable ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    "Load Table"
                  )}
                </Button>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={uploadTableData}
                  disabled={isArchivalMode || isUploading}
                >
                  {isUploading ? "Uploading..." : "Upload Table"}
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-white">Particulars</TableHead>
                    <TableHead className="text-white">Employees</TableHead>
                    <TableHead className="text-white w-16">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableFileNotFound ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-yellow-400 text-center py-8">
                        No table data found. Please calculate headcount reconciliation first, then upload the table.
                      </TableCell>
                    </TableRow>
                  ) : tableData ? (
                    <>
                      <TableRow className="bg-white/5">
                        <TableCell className="text-white">As per Above Headcount (A)</TableCell>
                        <TableCell className="text-white">
                          {tableData["As per Above Headcount (A)"]}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                      <TableRow className="bg-white/5">
                        <TableCell className="text-white">As per CTC Report (B)</TableCell>
                        <TableCell className="text-white">
                          {tableData["As per CTC Report (B)"]}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                      <TableRow className="bg-white/5">
                        <TableCell className="text-white">Difference (C)</TableCell>
                        <TableCell className="text-white">
                          {tableData["Difference (C)"]}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </>
                  ) : null}
                  {reconciliationRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="text-white">
                        <Input
                          type="text"
                          value={row.description}
                          onChange={(e) => updateReconciliationRow(row.id, 'description', e.target.value)}
                          placeholder="Description"
                          className="border-white/10 bg-black/40 text-white w-full"
                        />
                      </TableCell>
                      <TableCell className="text-white">
                        <Input
                          type="number"
                          value={row.amount}
                          onChange={(e) => updateReconciliationRow(row.id, 'amount', Number(e.target.value))}
                          placeholder="Amount"
                          className="border-white/10 bg-black/40 text-white w-full"
                        />
                      </TableCell>
                      <TableCell className="text-white">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeReconciliationRow(row.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} className="text-white">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={addReconciliationRow}
                        className="border-white/10 text-white hover:bg-white/10"
                        disabled={isArchivalMode}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Add Row
                      </Button>
                    </TableCell>
                  </TableRow>
                  {tableData && (
                    <TableRow className="bg-white/5">
                      <TableCell className="text-white font-medium">Net Difference</TableCell>
                      <TableCell className="text-white font-medium">
                        {(tableData["Difference (C)"] || 0) - reconciliationRows.reduce((sum, row) => sum + (row.amount || 0), 0)}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quarterly Weighted Average - only show after calculation */}
      {processingStatus === "completed" && (
        <Card className="border-white/10 bg-black/40">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-3 text-white">
                <TrendingUp className="h-5 w-5" />
                Quarterly Weighted Average Headcount
              </CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Quarterly Weighted Average Headcount"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-white">Quarter</TableHead>
                    <TableHead className="text-white">Weighted Figure (A)</TableHead>
                    <TableHead className="text-white">Total Weight (B)</TableHead>
                    <TableHead className="text-white">Weighted Average (A/B)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quarterlyData.map((quarter) => (
                    <TableRow key={quarter.quarter}>
                      <TableCell className="text-white">{quarter.quarter}</TableCell>
                      <TableCell className="text-white">{quarter.weightedFigure}</TableCell>
                      <TableCell className="text-white">{quarter.totalWeight}</TableCell>
                      <TableCell className="text-white font-medium">{quarter.weightedAverage}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Annual Weighted Average - only show after calculation */}
      {processingStatus === "completed" && (
        <Card className="border-white/10 bg-black/40">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-3 text-white">
                <TrendingUp className="h-5 w-5" />
                Annual Weighted Average Headcount
              </CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Annual Weighted Average Headcount"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-white">Year</TableHead>
                    <TableHead className="text-white">Weighted Figure (A)</TableHead>
                    <TableHead className="text-white">Total Weight (B)</TableHead>
                    <TableHead className="text-white">Weighted Average (A/B)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {annualData.map((year) => (
                    <TableRow key={year.year}>
                      <TableCell className="text-white">{year.year}</TableCell>
                      <TableCell className="text-white">{year.weightedFigure}</TableCell>
                      <TableCell className="text-white">{year.totalWeight}</TableCell>
                      <TableCell className="text-white font-medium">{year.weightedAverage}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Average Gross Pay - Quarterly - only show after calculation */}
      {processingStatus === "completed" && quarterlyPayData.length > 0 && (
        <Card className="border-white/10 bg-black/40">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-3 text-white">
                <LineChartIcon className="h-5 w-5" />
                Average Gross Pay - Quarterly
              </CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Average Gross Pay - Quarterly"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-white">Particulars</TableHead>
                    <TableHead className="text-white">Q4</TableHead>
                    <TableHead className="text-white">Q3</TableHead>
                    <TableHead className="text-white">Q2</TableHead>
                    <TableHead className="text-white">Q1</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="text-white">Gross Pay (A)</TableCell>
                    {(() => {
                      const quarterOrder = ['Q4', 'Q3', 'Q2', 'Q1'];
                      const orderedData = quarterOrder
                        .map(qKey => quarterlyPayData.find(q => q.quarter === qKey))
                        .filter((q): q is QuarterlyPayData => !!q);
                      return orderedData.map((q) => (
                        <TableCell key={q.quarter} className="text-white">{formatNumber(q.grossPay)}</TableCell>
                      ));
                    })()}
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-white">Weighted Average Headcount (B)</TableCell>
                    {(() => {
                      const quarterOrder = ['Q4', 'Q3', 'Q2', 'Q1'];
                      const orderedData = quarterOrder
                        .map(qKey => quarterlyPayData.find(q => q.quarter === qKey))
                        .filter((q): q is QuarterlyPayData => !!q);
                      return orderedData.map((q) => (
                        <TableCell key={q.quarter} className="text-white">{q.weightedAverageHeadcount}</TableCell>
                      ));
                    })()}
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-white font-medium">Average Pay (A/B)</TableCell>
                    {(() => {
                      const quarterOrder = ['Q4', 'Q3', 'Q2', 'Q1'];
                      const orderedData = quarterOrder
                        .map(qKey => quarterlyPayData.find(q => q.quarter === qKey))
                        .filter((q): q is QuarterlyPayData => !!q);
                      return orderedData.map((q) => (
                        <TableCell key={q.quarter} className="text-white font-medium">{formatNumber(q.averagePay)}</TableCell>
                      ));
                    })()}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Analytical Charts moved below Average Gross Pay - Quarterly */}
      {processingStatus === "completed" && showCharts && (
        <Card className="border-white/10 bg-black/40">
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-white">
              <div className="flex items-center gap-3">
                <BarChart3 className="h-5 w-5" />
                Analytical Charts
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Analytical Charts"))}
                  className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                  title="Review Comment"
                >
                  <MessageSquare className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowCharts(!showCharts)}
                  className="border-white/10"
                >
                  {showCharts ? "Hide Charts" : "Show Charts"}
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="quarter" stroke="#9CA3AF" />
                  <YAxis yAxisId="left" stroke="#9CA3AF" />
                  <YAxis yAxisId="right" orientation="right" stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: '1px solid #374151',
                      borderRadius: '6px',
                      color: '#F9FAFB'
                    }}
                  />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="linear"
                    dataKey="Average Gross Pay"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                  />
                  <Line
                    yAxisId="right"
                    type="linear"
                    dataKey="Weighted Average Headcount"
                    stroke="#10B981"
                    strokeWidth={2}
                    dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 rounded border border-blue-500/20 bg-blue-900/10 p-3 text-sm text-blue-200">
              <p className="font-medium">Analysis of the above data and trend:</p>
              <p className="mt-1">
                The quarterly trends show consistent growth in both average gross pay and headcount.
                The correlation between these metrics indicates stable compensation practices and
                organizational growth patterns.
              </p>
            </div>
          </CardContent>
        </Card>
      )}



      {/* Calculate Button */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-white">
            <Play className="h-5 w-5" />
            Execute Headcount Reconciliation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-gray-400">
              Display headcount reconciliation analysis or run a fresh reconciliation
            </div>
            <div className="flex gap-2">
              <Button
                onClick={displayHeadcountFromJson}
                disabled={processingStatus === "running"}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
              >
                Display Headcount Reconciliation
              </Button>
              <Button
                onClick={runHeadcountReconciliation}
                disabled={isArchivalMode || processingStatus === "running"}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {processingStatus === "running" ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Run Reconciliation
                  </>
                )}
              </Button>
            </div>
          </div>

          {processingStatus === "completed" && (
            <div className="rounded border border-green-500/20 bg-green-500/10 p-4">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-green-500" />
                <div>
                  <h4 className="font-medium text-green-300">Headcount Reconciliation Completed</h4>
                  <p className="text-sm text-green-200">
                    All calculations and analyses have been completed successfully
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === 'saved' && (
                <span className="text-sm text-green-400">Saved!</span>
              )}
              {inferenceSaveStatus === 'error' && inferenceSaveError && (
                <span className="text-sm text-red-400">Error</span>
              )}
              <Button
                onClick={saveInferenceQuestion}
                disabled={inferenceSaveStatus === 'saving' || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {inferenceSaveStatus === 'saving' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="payroll"
            itemId="HEADCOUNT-RECONCILIATION-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={false}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Select Source Files")]: "Select Source Files",
              [getSectionQuestionId("Map Date Columns")]: "Map Date Columns",
              [getSectionQuestionId("Opening Headcount")]: "Opening Headcount",
              [getSectionQuestionId("Monthly Headcount Reconciliation")]: "Monthly Headcount Reconciliation",
              [getSectionQuestionId("Test Reconciliation")]: "Test Reconciliation",
              [getSectionQuestionId("Quarterly Weighted Average Headcount")]: "Quarterly Weighted Average Headcount",
              [getSectionQuestionId("Annual Weighted Average Headcount")]: "Annual Weighted Average Headcount",
              [getSectionQuestionId("Average Gross Pay - Quarterly")]: "Average Gross Pay - Quarterly",
              [getSectionQuestionId("Analytical Charts")]: "Analytical Charts",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}
